import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ExternalLink, Eye, Link2, MapPin, MoreHorizontal, Pencil, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { useVirtualTourInventoryQuery, VirtualTourModel } from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { cn } from '@/lib/utils';
import { EligibilityBadges, fmtDate, LinkedListing, matterportShowUrl, StatusBadge } from './shared';
import { MatterportThumb, ModelPreviewDialog } from './ModelPreview';
import { AddressVisibilityDialog, ArchiveDialog, LinkDialog, ReactivateDialog, RenameDialog, VisibilityDialog } from './TourActionDialogs';

const RowMenu: React.FC<{ model: VirtualTourModel; onAct: (kind: 'link' | 'archive' | 'reactivate' | 'visibility' | 'details' | 'address', model: VirtualTourModel) => void }> = ({ model, onAct }) => {
  const publish = useGate(PERMISSIONS.virtualTour.publish).allowed;
  const edit = useGate(PERMISSIONS.virtualTour.edit).allowed;
  const link = useGate(PERMISSIONS.virtualTour.link).allowed;
  const archive = useGate(PERMISSIONS.virtualTour.archive).allowed;
  const reactivate = useGate(PERMISSIONS.virtualTour.reactivate).allowed;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <a href={matterportShowUrl(model.id)} target="_blank" rel="noopener noreferrer">
            <ExternalLink /> Open in Matterport
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!publish} onClick={() => onAct('visibility', model)}>
          <Eye /> Visibility
        </DropdownMenuItem>
        {model.vtid != null && (
          <DropdownMenuItem disabled={!edit} onClick={() => onAct('details', model)}>
            <Pencil /> Name and description
          </DropdownMenuItem>
        )}
        {model.vtid != null && (
          <DropdownMenuItem disabled={!publish} onClick={() => onAct('address', model)}>
            <MapPin /> Address visibility
          </DropdownMenuItem>
        )}
        {!model.eid && (
          <DropdownMenuItem disabled={!link} onClick={() => onAct('link', model)}>
            <Link2 /> Link
          </DropdownMenuItem>
        )}
        {model.state === 'active' && (
          <DropdownMenuItem disabled={!archive} onClick={() => onAct('archive', model)}>
            <Archive /> Archive
          </DropdownMenuItem>
        )}
        {model.state === 'inactive' && (
          <DropdownMenuItem disabled={!reactivate} onClick={() => onAct('reactivate', model)}>
            <RotateCcw /> Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const InventoryTab: React.FC = () => {
  const navigate = useNavigate();
  const { searchTerm, debouncedSearchTerm, filters, handleSearch, handleFilterChange } = useListFilters({
    initialFilters: { link: '', state: '' },
    namespace: 'inv',
  });
  const [refresh, setRefresh] = useState(false);
  const [act, setAct] = useState<{
    kind: 'link' | 'archive' | 'reactivate' | 'visibility' | 'details' | 'address';
    model: VirtualTourModel;
  } | null>(null);
  const [preview, setPreview] = useState<VirtualTourModel | null>(null);
  const closeAct = () => setAct(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useVirtualTourInventoryQuery({
    link: (filters.link || undefined) as 'linked' | 'unlinked' | undefined,
    state: filters.state || undefined,
    q: debouncedSearchTerm || undefined,
    refresh,
  });

  const handleRefresh = () => {
    setRefresh(true);
    refetch().finally(() => setRefresh(false));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by model name or id…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={filters.link}
          onChange={(e) => handleFilterChange('link', e.target.value)}
        >
          <option value="">All links</option>
          <option value="linked">Linked</option>
          <option value="unlinked">Unlinked</option>
        </select>
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={filters.state}
          onChange={(e) => handleFilterChange('state', e.target.value)}
        >
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <Button variant="outline" size="sm" className="ml-auto" onClick={handleRefresh} disabled={isFetching}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {isError ? (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
          {getApiErrorMessage(error, 'Could not load the Matterport inventory.')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" />
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Id</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">State</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Linked listing</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Visibility</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && !data
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3" colSpan={8}>
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                : (data?.models ?? []).map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <MatterportThumb id={m.id} alt={m.name ?? m.id} onClick={() => setPreview(m)} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{m.name || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{m.id}</td>
                      <td className="px-4 py-3">{m.state ? <StatusBadge status={m.state} /> : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(m.created)}</td>
                      <td className="px-4 py-2 text-left text-sm">
                        {m.eid ? (
                          <LinkedListing eid={m.eid} name={m.listing_name} onOpen={() => navigate(`/admin/listings/${m.eid}`)} />
                        ) : (
                          <span className="text-gray-400">Not linked</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {m.accessVisibility || m.visibility ? (
                            <StatusBadge status={m.accessVisibility || m.visibility} />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                          <EligibilityBadges planEligible={m.plan_eligible} source={m.source} />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <RowMenu model={m} onAct={(kind, model) => setAct({ kind, model })} />
                      </td>
                    </tr>
                  ))}
              {!isLoading && (data?.models?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                    No Matterport models match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data && <p className="text-xs text-gray-400">{data.total} model(s) total.</p>}

      <ModelPreviewDialog open={preview != null} onClose={() => setPreview(null)} id={preview?.id ?? ''} name={preview?.name ?? null} />
      <LinkDialog open={act?.kind === 'link'} onClose={closeAct} modelId={act?.model.id} />
      <ArchiveDialog
        open={act?.kind === 'archive'}
        onClose={closeAct}
        target={{ vtid: act?.model.vtid, model_id: act?.model.id }}
      />
      <ReactivateDialog
        open={act?.kind === 'reactivate'}
        onClose={closeAct}
        target={{ vtid: act?.model.vtid, model_id: act?.model.id }}
      />
      <VisibilityDialog
        open={act?.kind === 'visibility'}
        onClose={closeAct}
        target={{ vtid: act?.model.vtid, model_id: act?.model.id }}
        current={act?.model.accessVisibility || act?.model.visibility}
      />
      {act?.model.vtid != null && (
        <>
          <RenameDialog open={act.kind === 'details'} onClose={closeAct} vtid={act.model.vtid} name={act.model.name} />
          <AddressVisibilityDialog open={act.kind === 'address'} onClose={closeAct} vtid={act.model.vtid} />
        </>
      )}
    </div>
  );
};

export default InventoryTab;
